import { useEffect, useMemo, useState } from "react";
import {
  FileText, Eye, Download, Upload, Trash2, X, Filter,
  Pill, ClipboardList, MessageCircle, MessageSquarePlus, MapPin,
} from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "@/lib/toast";
import ApptNotesModal from "@/components/portal/ApptNotesModal";

const STATUS_OPTS = ["upcoming", "completed", "pending", "missed", "cancelled"];
const MAX_FILES = 10;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

export default function PatientMyAppointments({ onOpen }) {
  const [appts, setAppts] = useState([]);
  const [busy, setBusy] = useState(true);
  const [fSpec, setFSpec] = useState("");
  const [fDoc, setFDoc] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fDate, setFDate] = useState("");
  const [docsFor, setDocsFor] = useState(null); // appointment id whose docs modal is open
  const [waFor, setWaFor] = useState(null);     // appointment id whose WhatsApp modal is open
  const [notesFor, setNotesFor] = useState(null); // appointment id whose private-notes modal is open

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/appointments");
      setAppts(r.data.appointments || []);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const specialties = useMemo(() => Array.from(new Set(appts.map(a => a.specialty).filter(Boolean))).sort(), [appts]);
  const doctors = useMemo(() => Array.from(new Set(appts.map(a => a.doctor_name).filter(Boolean))).sort(), [appts]);

  const filtered = useMemo(() => appts.filter(a => {
    if (fSpec && a.specialty !== fSpec) return false;
    if (fDoc && a.doctor_name !== fDoc) return false;
    if (fStatus && a.status !== fStatus) return false;
    if (fDate && a.date !== fDate) return false;
    return true;
  }), [appts, fSpec, fDoc, fStatus, fDate]);

  const clearFilters = () => { setFSpec(""); setFDoc(""); setFStatus(""); setFDate(""); };

  const downloadRx = async (id) => {
    try {
      const res = await api.get(`/appointments/${id}/document?kind=prescription`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `prescription-${id}.pdf`; a.click();
    } catch (e) { toast(formatApiError(e.response?.data?.detail) || "Prescription not available yet."); }
  };

  const current = appts.find(a => a.id === docsFor) || null;
  const reload = async () => {
    const r = await api.get("/appointments");
    setAppts(r.data.appointments || []);
  };

  return (
    <div data-testid="patient-my-appts">
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Filter size={16} color="#185FA5" />
          <b style={{ color: "var(--ink)" }}>Filter appointments</b>
          {(fSpec || fDoc || fStatus || fDate) && (
            <button className="btn btn-ghost btn-sm" onClick={clearFilters} data-testid="appts-filter-clear">
              <X size={12} /> Clear
            </button>
          )}
        </div>
        <div className="appts-filter-row">
          <select className="in" value={fSpec} onChange={e => setFSpec(e.target.value)} data-testid="appts-filter-specialty">
            <option value="">All specialties</option>
            {specialties.map(s => <option key={s}>{s}</option>)}
          </select>
          <select className="in" value={fDoc} onChange={e => setFDoc(e.target.value)} data-testid="appts-filter-doctor">
            <option value="">All doctors</option>
            {doctors.map(d => <option key={d}>{d}</option>)}
          </select>
          <select className="in" value={fStatus} onChange={e => setFStatus(e.target.value)} data-testid="appts-filter-status">
            <option value="">All statuses</option>
            {STATUS_OPTS.map(s => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
          </select>
          <input className="in" type="date" value={fDate} onChange={e => setFDate(e.target.value)} data-testid="appts-filter-date" />
        </div>
      </div>

      {busy && <p className="lead">Loading your appointments…</p>}
      {!busy && filtered.length === 0 && (
        <div className="card" data-testid="appts-empty">
          <p className="lead" style={{ margin: 0 }}>
            {appts.length === 0
              ? "You have no appointments yet."
              : "No appointments match the selected filters."}
          </p>
        </div>
      )}

      {!busy && filtered.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table className="appts-table" data-testid="appts-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Specialty</th>
                <th>Doctor</th>
                <th>Status</th>
                <th>Diagnosis</th>
                <th>Prescription</th>
                <th>Documents</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} data-testid={`appts-row-${a.id}`}>
                  <td>
                    <div className="appts-date">
                      <b>{fmtDate(a.date)}</b>
                      <small>{a.time}</small>
                    </div>
                  </td>
                  <td>
                    {a.specialty || "—"}
                    {a.consult_type === "Clinic visit" && a.clinic_name && (
                      <small className="clinic-visit-chip" data-testid={`appts-clinic-${a.id}`}>
                        <MapPin size={11} /> {a.clinic_name}
                      </small>
                    )}
                  </td>
                  <td>
                    <button className="link-btn" onClick={() => onOpen(a.id)} data-testid={`appts-open-${a.id}`}>
                      {a.doctor_name || "—"}
                    </button>
                    {!a.for_self && (
                      <small className="forwho-chip" data-testid={`appts-forwho-${a.id}`}>
                        for {a.booked_for_relationship || "family"} — {a.patient_name}
                      </small>
                    )}
                  </td>
                  <td><span className={`badge ${a.status}`}>{a.status}</span></td>
                  <td style={{ maxWidth: 220 }}>
                    <span className="appts-diag" title={a.diagnosis || ""}>
                      {a.diagnosis || <span style={{ color: "var(--slate)" }}>—</span>}
                    </span>
                  </td>
                  <td>
                    {a.has_prescription ? (
                      <div className="rx-cell">
                        <button className="btn btn-ghost btn-sm" onClick={() => downloadRx(a.id)} data-testid={`appts-rx-${a.id}`}>
                          <Download size={13} /> PDF
                        </button>
                        <button className="btn btn-wa btn-sm" onClick={() => setWaFor(a.id)} title="Send to WhatsApp"
                                data-testid={`appts-wa-${a.id}`}>
                          <MessageCircle size={13} /> WhatsApp
                        </button>
                      </div>
                    ) : <span style={{ color: "var(--slate)" }}>—</span>}
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => setDocsFor(a.id)} data-testid={`appts-docs-${a.id}`}>
                      <FileText size={13} /> {(a.files || []).length}/10
                    </button>
                  </td>
                  <td>
                    <button
                      className="btn btn-ghost btn-sm appts-msg-btn"
                      onClick={() => setNotesFor(a.id)}
                      data-testid={`appts-msg-${a.id}`}
                      title="Send a private health-related note to this doctor"
                    >
                      <MessageSquarePlus size={13} /> Message
                      {a.note_unread > 0 && (
                        <span className="appts-msg-badge" data-testid={`appts-msg-badge-${a.id}`}>{a.note_unread}</span>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {current && (
        <DocsModal
          appt={current}
          onClose={() => setDocsFor(null)}
          onChange={reload}
        />
      )}

      {waFor && (
        <WhatsAppRxModal
          appt={appts.find(a => a.id === waFor)}
          onClose={() => setWaFor(null)}
        />
      )}

      {notesFor && (
        <ApptNotesModal
          appt={appts.find(a => a.id === notesFor)}
          role="patient"
          accent="#185FA5"
          onClose={() => { setNotesFor(null); reload(); }}
        />
      )}
    </div>
  );
}

function DocsModal({ appt, onClose, onChange }) {
  const files = appt.files || [];
  const remaining = MAX_FILES - files.length;
  const canModify = appt.status !== "completed" && appt.status !== "cancelled";

  const upload = async (e) => {
    const fs = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fs.length) return;
    if (fs.length > remaining) {
      toast(`You can add only ${remaining} more (max ${MAX_FILES} per consultation).`); return;
    }
    const fd = new FormData(); fs.forEach(f => fd.append("files", f));
    try {
      await api.post(`/appointments/${appt.id}/files`, fd);
      toast("Documents uploaded ✓"); onChange();
    } catch (err) { toast(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (fid, fname) => {
    if (!window.confirm(`Remove "${fname}"? You can re-upload a new copy.`)) return;
    try {
      await api.delete(`/appointments/${appt.id}/files/${fid}`);
      toast("Document removed ✓"); onChange();
    } catch (err) { toast(formatApiError(err.response?.data?.detail)); }
  };

  const view = (fid) => window.open(`${API}/files/${fid}`, "_blank");

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} data-testid="docs-modal" style={{ maxWidth: 540 }}>
        <button className="m-close" onClick={onClose} data-testid="docs-close">✕</button>
        <h3 className="h" style={{ fontSize: 20, marginTop: 0 }}>
          <ClipboardList size={18} style={{ verticalAlign: "-3px" }} /> Your documents
        </h3>
        <p className="lead" style={{ marginTop: 0, marginBottom: 8 }}>
          {appt.doctor_name} · {fmtDate(appt.date)} · {appt.time}
        </p>
        <p className="prof-hint" style={{ marginTop: 0 }}>
          Only you and the consulting doctor can see these. Up to {MAX_FILES} documents per consultation.
        </p>

        {files.length === 0 && (
          <p className="lead" style={{ margin: "12px 0" }}>No documents uploaded yet.</p>
        )}
        <div className="doc-files" style={{ flexDirection: "column" }}>
          {files.map(f => (
            <div className="doc-file-row" key={f.file_id} data-testid={`doc-${f.file_id}`}>
              <FileText size={16} color="#185FA5" />
              <span className="dfr-name" title={f.filename}>{f.filename}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => view(f.file_id)} title="View"
                      data-testid={`doc-view-${f.file_id}`}><Eye size={13} /></button>
              {canModify && (
                <button className="btn btn-ghost btn-sm danger" onClick={() => remove(f.file_id, f.filename)}
                        title="Delete" data-testid={`doc-del-${f.file_id}`}><Trash2 size={13} /></button>
              )}
            </div>
          ))}
        </div>

        {canModify && remaining > 0 && (
          <label className="upload" data-testid="docs-upload" style={{ marginTop: 12 }}>
            <input type="file" multiple hidden onChange={upload} />
            <Upload size={20} color="#185FA5" />
            <p>Upload documents ({remaining} more allowed)</p>
            <small>PDF, JPG or PNG</small>
          </label>
        )}
        {!canModify && (
          <p className="prof-hint" style={{ marginTop: 12 }}>
            This consultation is {appt.status}. Documents are read-only.
          </p>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------
   "WhatsApp my Rx" modal
   - Prefills the patient's saved mobile (editable).
   - On submit: calls POST /appointments/{id}/whatsapp-rx.
     • If Twilio WhatsApp is configured server-side → message sent
       directly (channel === 'twilio').
     • Otherwise the server returns a wa.me deep-link the browser
       opens (channel === 'walink').
   --------------------------------------------------------------------- */
function WhatsAppRxModal({ appt, onClose }) {
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {channel, url}
  // Prefill the saved mobile once
  useEffect(() => {
    api.get("/auth/me").then(r => setMobile(r.data.user?.mobile || ""))
      .catch(() => {});
  }, []);

  if (!appt) return null;

  const submit = async () => {
    if (!mobile.trim()) { toast("Please enter the WhatsApp number."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/appointments/${appt.id}/whatsapp-rx`, { mobile });
      if (data.channel === "twilio") {
        toast("Sent to WhatsApp ✓");
        setDone({ channel: "twilio" });
      } else {
        // wa.me fallback — open it in a new tab
        window.open(data.url, "_blank", "noopener,noreferrer");
        setDone({ channel: "walink", url: data.url });
      }
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal wa-modal" onClick={e => e.stopPropagation()} data-testid="wa-rx-modal">
        <button className="m-close" onClick={onClose} data-testid="wa-rx-close">✕</button>
        <h3 className="h" style={{ fontSize: 20, marginTop: 0, color: "#1D9E75" }}>
          <MessageCircle size={18} style={{ verticalAlign: "-3px" }} /> Send my prescription on WhatsApp
        </h3>
        <p className="lead" style={{ marginTop: 0 }}>
          {appt.doctor_name} · {appt.date} · {appt.time}
        </p>

        {!done && (
          <>
            <div className="field">
              <label>WhatsApp number</label>
              <input className="in" value={mobile} onChange={e => setMobile(e.target.value)}
                     placeholder="+91 98765 43210" data-testid="wa-rx-mobile" />
              <small style={{ color: "var(--slate)", fontSize: 12 }}>
                We&apos;ll send your prescription PDF to this number. By default we use the mobile saved on your profile.
              </small>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-wa" onClick={submit} disabled={busy} data-testid="wa-rx-send">
                <MessageCircle size={14} /> {busy ? "Sending…" : "Send on WhatsApp"}
              </button>
            </div>
          </>
        )}

        {done && done.channel === "twilio" && (
          <div className="banner info" data-testid="wa-rx-sent">
            ✓ &nbsp; Your prescription has been sent to WhatsApp. Please check your messages.
          </div>
        )}
        {done && done.channel === "walink" && (
          <div data-testid="wa-rx-fallback">
            <div className="banner info" style={{ marginBottom: 10 }}>
              We&apos;ve opened WhatsApp with your prescription ready to send. If it didn&apos;t open, use the button below.
            </div>
            <a className="btn btn-wa" href={done.url} target="_blank" rel="noreferrer">
              <MessageCircle size={14} /> Open WhatsApp
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
